<script lang="ts">
  import { Usuarios, type TenantUser } from '../lib/api';
  import BrandingAdminPanel from './BrandingAdminPanel.svelte';
  import FirmasMedicosPanel from './FirmasMedicosPanel.svelte';
  import { tenantCtx } from '../lib/tenant.svelte';
  import { effectiveOrgTipo } from '../lib/org-context.svelte';
  import { filterAssignableRoles } from '../lib/tenant-roles';

  type AdminTab = 'usuarios' | 'marca' | 'firmas';
  let adminTab = $state<AdminTab>('usuarios');

  const ROL_LABEL: Record<string, string> = {
    'admin-tenant': 'Administrador', 'recepcion': 'Recepción', 'medico': 'Médico',
    'enfermeria': 'Enfermería', 'farmacia': 'Farmacia', 'laboratorio': 'Laboratorio',
    'imagenologia': 'Imagenología', 'quirofano': 'Quirófano', 'caja': 'Caja',
    'tesoreria': 'Tesorería', 'bodega': 'Bodega', 'contabilidad': 'Contabilidad',
    'nomina': 'Nómina', 'mspas': 'MSPAS', 'auditoria': 'Auditoría',
    'consultorio-externo': 'Consultorio externo', 'vet-medico': 'Vet · Médico',
    'vet-recepcion': 'Vet · Recepción', 'vet-farmacia': 'Vet · Farmacia',
  };
  const label = (r: string) => {
    if (r === 'medico' && orgEfectivo === 'clinica-fisioterapia') return 'Fisioterapista';
    if (r === 'medico' && orgEfectivo === 'clinica-nutricion') return 'Nutricionista';
    if (r === 'medico' && orgEfectivo === 'clinica-psicologia') return 'Psicólogo/a';
    return ROL_LABEL[r] ?? r;
  };

  let lista = $state<TenantUser[]>([]);
  let rolesDisponibles = $state<string[]>([]);
  let cargando = $state(false);
  let error = $state<string | null>(null);
  let aviso = $state<string | null>(null);

  // Alta
  let nuevo = $state<{ usuario: string; nombre: string; apellido: string; email: string; roles: string[] }>(
    { usuario: '', nombre: '', apellido: '', email: '', roles: [] });
  let credencial = $state<{ usuario: string; tempPassword: string } | null>(null);

  // Edición de roles inline
  let editandoId = $state<string | null>(null);
  let rolesEdit = $state<string[]>([]);

  const orgEfectivo = $derived(
    effectiveOrgTipo(tenantCtx.orgTipo as import('../lib/navigation').OrgTipo | null, tenantCtx.isHospitalEdition),
  );

  async function recargar() {
    cargando = true; error = null;
    try {
      const [u, r] = await Promise.all([Usuarios.list(), Usuarios.rolesAsignables()]);
      lista = u.users ?? [];
      rolesDisponibles = filterAssignableRoles(r.roles ?? [], orgEfectivo);
    } catch (e: any) {
      error = traducir(e.message);
    }
    cargando = false;
  }

  function traducir(msg: string): string {
    if (!msg) return 'Error';
    if (msg.includes('requiere-modo-keycloak')) return 'La gestión de usuarios requiere iniciar sesión con Keycloak.';
    if (msg.includes('rol-admin-tenant-requerido')) return 'Necesitas el rol de Administrador para gestionar usuarios.';
    if (msg.includes('keycloak-admin-deshabilitado')) return 'El provisioning de Keycloak está deshabilitado en el servidor.';
    if (msg.includes('usuario-ya-existe')) return 'Ya existe un usuario con ese identificador.';
    if (msg.includes('usuario-de-otro-tenant')) return 'No puedes gestionar usuarios de otra organización.';
    return msg;
  }

  function toggleRol(arr: string[], r: string): string[] {
    return arr.includes(r) ? arr.filter(x => x !== r) : [...arr, r];
  }

  async function crear() {
    error = null; aviso = null; credencial = null;
    if (!nuevo.usuario.trim()) { error = 'El identificador de usuario es obligatorio.'; return; }
    try {
      const r = await Usuarios.crear({
        usuario: nuevo.usuario.trim(), nombre: nuevo.nombre.trim(),
        apellido: nuevo.apellido.trim(), email: nuevo.email.trim(), roles: nuevo.roles,
      });
      credencial = { usuario: r.usuario, tempPassword: r.tempPassword };
      nuevo = { usuario: '', nombre: '', apellido: '', email: '', roles: [] };
      await recargar();
    } catch (e: any) { error = traducir(e.message); }
  }

  function empezarEdicion(u: TenantUser) {
    editandoId = u.id; rolesEdit = [...u.roles];
  }
  async function guardarRoles(u: TenantUser) {
    error = null;
    try { await Usuarios.setRoles(u.id, rolesEdit); editandoId = null; await recargar(); }
    catch (e: any) { error = traducir(e.message); }
  }
  async function toggleEnabled(u: TenantUser) {
    error = null;
    try { await Usuarios.setEnabled(u.id, !u.enabled); await recargar(); }
    catch (e: any) { error = traducir(e.message); }
  }
  async function resetPwd(u: TenantUser) {
    error = null; aviso = null; credencial = null;
    try {
      const r = await Usuarios.resetPassword(u.id);
      credencial = { usuario: u.username, tempPassword: r.tempPassword };
    } catch (e: any) { error = traducir(e.message); }
  }

  async function copiar(txt: string) {
    try { await navigator.clipboard.writeText(txt); aviso = 'Copiado al portapapeles.'; }
    catch { aviso = null; }
  }

  $effect(() => { if (adminTab === 'usuarios') recargar(); });
</script>

<section>
  <h2>Administración</h2>
  <p class="sub">Usuarios, roles, identidad visual y firmas médicas</p>
  <div class="tabs">
    <button type="button" class:active={adminTab === 'usuarios'} onclick={() => adminTab = 'usuarios'}>Usuarios y roles</button>
    <button type="button" class:active={adminTab === 'marca'} onclick={() => adminTab = 'marca'}>Identidad visual</button>
    <button type="button" class:active={adminTab === 'firmas'} onclick={() => adminTab = 'firmas'}>Firmas médicas</button>
  </div>

  {#if adminTab === 'marca'}
    <BrandingAdminPanel />
  {:else if adminTab === 'firmas'}
    <FirmasMedicosPanel />
  {:else}
  <h3>Usuarios y roles</h3>
  <p class="hint">Crea el personal de tu clínica y asígnale roles acordes a su rubro.
    Cada usuario recibe una contraseña temporal que deberá cambiar en su primer ingreso.</p>

  {#if error}<div class="err">{error}</div>{/if}
  {#if aviso}<div class="ok">{aviso}</div>{/if}

  {#if credencial}
    <div class="cred">
      <strong>Credencial generada (se muestra una sola vez)</strong>
      <div class="cred-row"><span>Usuario:</span> <code>{credencial.usuario}</code>
        <button onclick={() => copiar(credencial!.usuario)}>Copiar</button></div>
      <div class="cred-row"><span>Contraseña temporal:</span> <code>{credencial.tempPassword}</code>
        <button onclick={() => copiar(credencial!.tempPassword)}>Copiar</button></div>
      <button class="cerrar" onclick={() => credencial = null}>Entendido</button>
    </div>
  {/if}

  <h3>Nuevo usuario</h3>
  <div class="form">
    <input placeholder="Identificador (ej. maria.lopez)" bind:value={nuevo.usuario}/>
    <input placeholder="Nombre" bind:value={nuevo.nombre}/>
    <input placeholder="Apellido" bind:value={nuevo.apellido}/>
    <input placeholder="Email (opcional)" bind:value={nuevo.email}/>
  </div>
  <div class="roles-pick">
    {#each rolesDisponibles as r (r)}
      <label class="rol">
        <input type="checkbox" checked={nuevo.roles.includes(r)}
               onchange={() => nuevo.roles = toggleRol(nuevo.roles, r)}/>
        <span>{label(r)}</span>
      </label>
    {/each}
  </div>
  <button class="primary" onclick={crear}>Crear usuario</button>

  <h3>Personal ({lista.length})</h3>
  <div class="filtros"><button onclick={recargar} disabled={cargando}>Recargar</button></div>
  <table>
    <thead><tr><th>Usuario</th><th>Nombre</th><th>Roles</th><th>Estado</th><th>Acciones</th></tr></thead>
    <tbody>
      {#each lista as u (u.id)}
        <tr class:inactivo={!u.enabled}>
          <td><code>{u.username}</code></td>
          <td>{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</td>
          <td>
            {#if editandoId === u.id}
              <div class="roles-pick small">
                {#each rolesDisponibles as r (r)}
                  <label class="rol">
                    <input type="checkbox" checked={rolesEdit.includes(r)}
                           onchange={() => rolesEdit = toggleRol(rolesEdit, r)}/>
                    <span>{label(r)}</span>
                  </label>
                {/each}
              </div>
            {:else}
              {#if u.roles.length}
                {#each u.roles as r}<span class="tag">{label(r)}</span>{/each}
              {:else}<span class="muted">sin roles</span>{/if}
            {/if}
          </td>
          <td>{u.enabled ? 'Activo' : 'Inactivo'}</td>
          <td class="acciones">
            {#if editandoId === u.id}
              <button class="primary" onclick={() => guardarRoles(u)}>Guardar</button>
              <button onclick={() => editandoId = null}>Cancelar</button>
            {:else}
              <button onclick={() => empezarEdicion(u)}>Roles</button>
              <button onclick={() => toggleEnabled(u)}>{u.enabled ? 'Desactivar' : 'Activar'}</button>
              <button onclick={() => resetPwd(u)}>Reset clave</button>
            {/if}
          </td>
        </tr>
      {/each}
      {#if lista.length === 0 && !cargando}
        <tr><td colspan="5" class="muted">Sin usuarios todavía.</td></tr>
      {/if}
    </tbody>
  </table>
  {/if}
</section>

<style>
  section { padding: 1rem; }
  h2 { margin: 0 0 0.2rem; font-size: 1.4rem; color: #111827; }
  .sub { margin: 0 0 1rem; color: #6b7280; font-size: 0.85rem; }
  .tabs { display: flex; gap: 0.35rem; flex-wrap: wrap; margin: .8rem 0 1.2rem; border-bottom: 1px solid #e5e7eb; padding-bottom: .5rem; }
  .tabs button {
    padding: 0.5rem 0.95rem; border: 1px solid #e5e7eb; border-radius: 8px 8px 0 0;
    background: #fff; color: #374151; cursor: pointer; font: inherit; font-size: 0.85rem; font-weight: 600;
  }
  .tabs button:hover { background: #f0fdf4; color: #16a34a; }
  .tabs button.active { background: #16a34a; color: #fff; border-color: #16a34a; }
  h3 { margin: 0 0 0.5rem; color: #166534; }
  .hint { color: #6b7280; font-size: .88rem; margin: .2rem 0 .8rem; }
  .err { background: #fee2e2; color: #dc2626; padding: .6rem; border-radius: 8px; margin: .4rem 0; }
  .ok { background: #dcfce7; color: #166534; padding: .6rem; border-radius: 8px; margin: .4rem 0; }
  .cred { background: #fef3c7; border: 1px solid #fbbf24; border-radius: 8px; padding: .8rem 1rem; margin: .6rem 0; color: #111827; }
  .cred-row { display: flex; align-items: center; gap: .5rem; margin: .35rem 0; }
  .cred-row span { min-width: 150px; color: #6b7280; }
  .cred code { background: #fff; border: 1px solid #e5e7eb; padding: .15rem .5rem; border-radius: 6px; font-size: .95rem; }
  .cred .cerrar { margin-top: .4rem; }
  .filtros, .form { display: flex; gap: .6rem; flex-wrap: wrap; align-items: end; margin: .6rem 0; }
  .form input { padding: .5rem .65rem; border: 1px solid #e5e7eb; border-radius: 8px; min-width: 180px; background: #fff; color: #111827; }
  .form input:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  .roles-pick { display: flex; gap: .4rem; flex-wrap: wrap; margin: .4rem 0 .8rem; }
  .roles-pick.small { margin: 0; }
  .rol { display: inline-flex; align-items: center; gap: .4rem; border: 1px solid #e5e7eb; border-radius: 8px;
         padding: .35rem .6rem; font-size: .82rem; cursor: pointer; color: #111827; background: #fff; }
  .rol input { accent-color: #16a34a; margin: 0; }
  button { padding: .4rem .8rem; border: 1px solid #16a34a; background: #fff; color: #15803d;
           border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; }
  button:hover { background: #f0fdf4; }
  button.primary { background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; box-shadow: 0 4px 14px rgba(22,163,74,0.3); }
  table { width: 100%; border-collapse: collapse; margin-top: .6rem; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .5rem .6rem; background: #f0fdf4; }
  td { border-top: 1px solid #f3f4f6; padding: .5rem .6rem; text-align: left; vertical-align: top; color: #111827; }
  tr.inactivo { color: #9ca3af; }
  .tag { display: inline-block; color: #166534; font-size: .8rem; margin: .1rem .2rem .1rem 0; }
  .muted { color: #9ca3af; }
  .acciones { display: flex; gap: .35rem; flex-wrap: wrap; }
  code { background: #f3f4f6; padding: .1rem .35rem; border-radius: 4px; font-size: .8rem; color: #111827; }
</style>
